/**
 * Onda Aves — the seabirds (client/render-only, ZERO wire, ZERO sim impact).
 *
 * The old flock was 11 Groups × 2 wing meshes = 22 draw calls for eleven dark crosses that circled the camera
 * on rails at 60–90 m, flapping both wings in lockstep. This rebuild is ONE draw call for the whole flock and
 * actually reads as gulls:
 *
 *   • ONE InstancedMesh, one custom shader. The wing beat is done in the VERTEX shader from a per-instance
 *     phase + rate, so 24 birds animate for free (no per-wing matrices, no CPU rotation, no GC).
 *   • A real gull silhouette: swept wing with an elbow (inner arm + hand), a slim body and a wedge tail — the
 *     "M" you recognise from below, not two flat planks.
 *   • The beat is a GULL beat, not a metronome: a fast down-stroke, a slower recovery, the tip lagging the arm
 *     (the shader delays the outer segment), and long GLIDES between bursts — the single most convincing part
 *     of a real gull.
 *   • Flight is boids-lite on the CPU at 8 Hz (cohesion toward a slowly wandering flock target, separation,
 *     wander) integrated per frame; a turn BANKS the bird (roll ∝ turn rate) and a banked bird loses lift and
 *     dips — so curves look flown, not scripted.
 *   • Birds cruise at mixed altitudes and some skim the wave tops, so you actually SEE them from the deck; they
 *     scatter (climb + fade) as a storm builds and go to roost at night.
 */
import * as THREE from 'three';

/** Tuning — render-only, so it lives with the code that draws it. */
export const BIRDS = {
  COUNT: 22,
  /** Wingspan (m) before per-bird size variation, and the size spread. */
  SPAN: 3.0,
  SIZE_MIN: 0.7,
  SIZE_MAX: 1.35,
  /** Cruise speed (m/s) and how far the flock ranges from the camera (m). */
  SPEED_MIN: 7,
  SPEED_MAX: 13,
  RANGE: 120,
  /** Altitude band above the sea (m) — the low end is what makes them visible from the deck. */
  ALT_MIN: 7,
  ALT_MAX: 62,
  /** Steering weights (boids-lite). */
  COHESION: 0.55,
  SEPARATION: 1.5,
  SEP_DIST: 16,
  WANDER: 0.5,
  /** Max turn rate (rad/s) and the roll a full-rate turn banks into. */
  TURN_RATE: 0.85,
  BANK: 1.05,
  /** Steering tick (Hz) — the flock only *thinks* this often; motion is integrated every frame. */
  THINK_HZ: 8,
  /** How far out from an islet's centre the birds hold their circuit (× island radius, + this many metres). */
  ISLE_ORBIT_MUL: 1.5,
  ISLE_ORBIT_ADD: 26,
  /** Radians/second a sub-flock walks around the islet it has adopted (a slow, lazy circuit). */
  ISLE_ORBIT_RATE: 0.19,
  /** Metres from the camera inside which an islet is worth circling (past it you cannot read the birds). */
  ISLE_RANGE: 260,
  /** WIND. How hard a crosswind crabs the nose upwind, banks the windward wing down, and speeds a bird up
   *  running downwind / slows one beating into it. This is the whole "read the wind with no UI" cue. */
  WIND_CRAB: 0.46,
  WIND_BANK: 0.44,
  WIND_DRIVE: 0.24,
} as const;

/** One bird's CPU state. Position/velocity are world (x, y, z); the shader does everything else. */
export interface Bird {
  x: number; y: number; z: number;
  heading: number;   // world yaw (rad)
  speed: number;
  alt: number;       // preferred altitude above the sea
  roll: number;      // current bank (rad)
  turn: number;      // current turn rate (rad/s, smoothed)
  size: number;
  phase: number;     // wing-beat phase offset
  rate: number;      // wing-beat rate (Hz-ish)
  glide: number;     // 0 = beating hard … 1 = pure glide
  glideT: number;    // seconds until the next beat/glide switch
  wander: number;    // current wander bias
}

/**
 * PURE steering step for one bird (unit-tested): cohesion toward `(cx, cz)`, separation from the flock's
 * average near-neighbour push `(sx, sz)`, plus its own wander. Returns the new heading/turn — no THREE, no
 * randomness beyond the caller-supplied wander, so a test can assert it converges and never spins.
 */
export function steerBird(b: Bird, cx: number, cz: number, sx: number, sz: number, dt: number): void {
  const toC = Math.atan2(cz - b.z, cx - b.x);
  const dC = normPi(toC - b.heading);
  let want = dC * BIRDS.COHESION;
  if (sx !== 0 || sz !== 0) {
    const away = Math.atan2(sz, sx);
    want += normPi(away - b.heading) * BIRDS.SEPARATION;
  }
  want += b.wander * BIRDS.WANDER;
  const target = Math.max(-BIRDS.TURN_RATE, Math.min(BIRDS.TURN_RATE, want));
  // smooth the turn so a bird banks INTO a curve instead of snapping onto a new bearing
  b.turn += (target - b.turn) * Math.min(1, dt * 3.2);
  b.heading = normPi(b.heading + b.turn * dt);
  // bank ∝ turn rate; a banked bird sheds lift and sinks a touch (then climbs back on the roll-out)
  const wantRoll = -(b.turn / BIRDS.TURN_RATE) * BIRDS.BANK;
  b.roll += (wantRoll - b.roll) * Math.min(1, dt * 4.5);
}

/** Wrap to (−π, π]. */
function normPi(a: number): number { return ((a + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI; }

/**
 * How a live wind bends one bird, as pure numbers (unit-tested — no THREE, no flock state).
 *
 * This is the owner's "leitura de vento sem UI": you should be able to look at the gulls and know which way
 * it is blowing, the way you can at a real harbour. Three things do it, and all three are what a real bird
 * has to do rather than decoration:
 *
 *   crab  — a bird holding a track across the wind points its NOSE upwind of where it is going. This is the
 *           single most legible cue, because the mismatch between "where it is heading" and "where it is
 *           moving" is visible from any angle.
 *   bank  — it drops the windward wing to hold that crab.
 *   drive — running downwind it fairly races; beating into it, it crawls. The flock stretches out along
 *           the wind axis on its own as a result, which is a fourth cue nobody had to author.
 *
 * `track` is the bird's course, `windDir` where the wind is blowing TOWARD, `ws` its strength (≈1 nominal).
 * Returns radians of yaw offset, radians of wing bank, and a speed multiplier.
 */
export function windTrim(track: number, windDir: number, ws: number): { crab: number; bank: number; drive: number } {
  const w = Math.max(0, Math.min(1.7, ws));
  const rel = normPi(windDir - track);
  const cross = Math.sin(rel);     // +1 = wind pushing from the bird's left
  const along = Math.cos(rel);     // +1 = dead downwind, −1 = beating into it
  return {
    crab: -cross * BIRDS.WIND_CRAB * w,
    bank: cross * BIRDS.WIND_BANK * w,
    drive: 1 + along * BIRDS.WIND_DRIVE * w,
  };
}

/**
 * Gull geometry (shared by the sky flock and the fishing-shoal gulls): nose along +X, wings along ±Z, flat in
 * the XZ plane so it reads from BELOW (where players see it). Per-vertex `aSpan` (0 body … 1 tip) and `aSeg`
 * (0 inner arm, 1 outer hand) let the vertex shader fold the wing like a real one. 8 triangles.
 */
export function buildGullGeometry(span: number = BIRDS.SPAN): THREE.BufferGeometry {
  const s = span * 0.5;
  const pos: number[] = [], aSpan: number[] = [], aSeg: number[] = [];
  const push = (x: number, y: number, z: number, sp: number, sg: number): void => { pos.push(x, y, z); aSpan.push(sp); aSeg.push(sg); };
  const tri = (
    a: readonly [number, number, number], b: readonly [number, number, number], c: readonly [number, number, number],
    sp: readonly [number, number, number], sg: number,
  ): void => { push(a[0], a[1], a[2], sp[0], sg); push(b[0], b[1], b[2], sp[1], sg); push(c[0], c[1], c[2], sp[2], sg); };
  // --- body: a narrow fuselage (head forward of the shoulders, tail wedge behind) ---
  const NOSE: [number, number, number] = [s * 0.46, 0.01, 0], HIPL: [number, number, number] = [-s * 0.14, 0.02, s * 0.055], HIPR: [number, number, number] = [-s * 0.14, 0.02, -s * 0.055];
  tri(NOSE, HIPL, HIPR, [0, 0, 0], 0);
  tri(HIPL, [-s * 0.40, 0.01, 0], HIPR, [0, 0, 0], 0);
  // tail: a small fan that reads at any angle
  tri([-s * 0.30, 0, s * 0.09], [-s * 0.55, 0, 0], [-s * 0.30, 0, -s * 0.09], [0, 0, 0], 0);
  // --- wings, mirrored. HIGH ASPECT RATIO is the whole game: a long, narrow, swept blade whose chord
  //     collapses to a point at the tip. A wide rectangular wing reads as a paper plane, never as a gull. ---
  for (const sgn of [1, -1]) {
    const elbow = s * 0.45, tip = s * 1.0;
    // inner arm (shoulder → elbow): leading edge sweeps a little forward, chord already narrow
    const sL: [number, number, number] = [s * 0.17, 0, sgn * s * 0.05], sT: [number, number, number] = [-s * 0.15, 0, sgn * s * 0.05];
    const eL: [number, number, number] = [s * 0.13, 0, sgn * elbow], eT: [number, number, number] = [-s * 0.11, 0, sgn * elbow];
    tri(sL, eL, sT, [0.06, 0.45, 0.06], 0);
    tri(sT, eL, eT, [0.06, 0.45, 0.45], 0);
    // outer hand (elbow → tip): swept BACK, chord tapering to a fine point — the gull's scythe
    const tL: [number, number, number] = [-s * 0.05, 0, sgn * tip], tT: [number, number, number] = [-s * 0.13, 0, sgn * tip * 0.985];
    tri(eL, tL, eT, [0.45, 1.0, 0.45], 1);
    tri(eT, tL, tT, [0.45, 1.0, 0.98], 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('aSpan', new THREE.BufferAttribute(new Float32Array(aSpan), 1));
  g.setAttribute('aSeg', new THREE.BufferAttribute(new Float32Array(aSeg), 1));
  return g;
}

/**
 * The gull material: the WING BEAT lives here. Per instance we ship `aFlap = (phase, rate, glide, dark)`; the
 * vertex shader lifts each wing vertex by an asymmetric beat curve scaled by span² (the tip travels far, the
 * shoulder barely moves) with the outer hand LAGGING the arm — the whip a real wingbeat has. `glide` blends the
 * whole thing toward a fixed dihedral V. One draw call for every bird on screen.
 */
export function buildGullMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    // three's fog uniforms are merged in (and the fog chunks included below) so a distant gull fades into the
    // same haze the sea does — without them the renderer's refreshFogUniforms would throw on this material.
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      {
        uTime: { value: 0 },
        uTop: { value: new THREE.Color(0xf2f6fa) },     // sunlit back
        uUnder: { value: new THREE.Color(0x9fb0bf) },   // shaded underside
        uOpacity: { value: 1 },
      },
    ]),
    vertexShader: /* glsl */ `
      #include <common>
      #include <fog_pars_vertex>
      attribute float aSpan, aSeg;
      attribute vec4 aFlap;              // x: phase, y: rate, z: glide 0..1, w: shade bias
      uniform float uTime;
      varying float vUp, vShade, vSpan;
      void main(){
        vec3 p = position;
        float ph = uTime * aFlap.y + aFlap.x - aSeg * 0.55;         // the hand lags the arm -> the wing whips
        // asymmetric beat: fast down-stroke, slower recovery (a sine is too metronomic to read as a bird)
        float s = sin(ph);
        float beat = s > 0.0 ? pow(s, 0.65) : -pow(-s, 1.45);
        float amp = mix(0.62, 0.10, aFlap.z);                        // gliding = a held dihedral, barely moving
        float lift = beat * amp * aSpan * aSpan + 0.13 * aSpan;      // + the resting V (reads as a gull from below)
        p.y += lift * abs(p.z) * 1.15;
        p.z *= 1.0 - 0.16 * max(0.0, beat) * aSpan;                  // wings shorten a touch at the top of the beat
        vUp = lift;
        vShade = aFlap.w;
        vSpan = aSpan;
        #ifdef USE_INSTANCING
          vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(p, 1.0);
        #else
          vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);
        #endif
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      precision mediump float;
      #include <common>
      #include <fog_pars_fragment>
      uniform vec3 uTop, uUnder; uniform float uOpacity;
      varying float vUp, vShade, vSpan;
      void main(){
        // a wing raised above the body catches the sky; a lowered one is in its own shadow
        vec3 col = mix(uUnder, uTop, clamp(0.5 + vUp * 0.8, 0.0, 1.0)) * (1.0 - vShade * 0.30);
        // the black wingtips — the ONE marking that makes a 20-pixel shape read as "gull" and not "bug"
        col = mix(col, vec3(0.13, 0.15, 0.18), smoothstep(0.70, 0.96, vSpan));
        if (uOpacity < 0.01) discard;
        gl_FragColor = vec4(pow(col, vec3(2.2)), uOpacity);
        #include <fog_fragment>
      }
    `,
    transparent: true, depthWrite: true, side: THREE.DoubleSide, fog: true,
  });
}

/**
 * The sky flock: one InstancedMesh of gulls wheeling near the camera. Cheap enough to keep on a phone (1 draw
 * call, ~350 tris for the whole flock) and it is the main "the sea is alive" cue by day.
 */
export class BirdFlock {
  readonly mesh: THREE.InstancedMesh;
  private mat: THREE.ShaderMaterial;
  private birds: Bird[] = [];
  private flap: Float32Array;
  private dummy = new THREE.Object3D();
  private thinkAcc = 0;
  /** Three sub-flock marks (x, z, next-retarget time) — birds steer toward the one for `i % 3`. */
  private gTargetX = [0, 0, 0];
  private gTargetZ = [0, 0, 0];
  private gTargetT = [0, 0, 0];
  /** Per sub-flock: the islet it has adopted (−1 = none, it is just wandering) and where it is on the
   *  circuit. A group with an islet re-derives its mark EVERY frame from an advancing angle, which is what
   *  turns "fly toward a point" into "circle the island" without a single line of new steering code. */
  private gIsle = [-1, -1, -1];
  private gIsleAng = [0, 0, 0];
  /** Islets worth circling, in world (x, z, radius) — fed by the renderer from the live world. */
  private isles: { x: number; z: number; r: number }[] = [];
  private _opacity = 0;

  constructor(parent: THREE.Object3D, count = BIRDS.COUNT) {
    const geo = buildGullGeometry();
    this.mat = buildGullMaterial();
    this.mesh = new THREE.InstancedMesh(geo, this.mat, count);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.flap = new Float32Array(count * 4);
    geo.setAttribute('aFlap', new THREE.InstancedBufferAttribute(this.flap, 4).setUsage(THREE.DynamicDrawUsage));
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2, r = 60 + (i % 5) * 26;
      const size = BIRDS.SIZE_MIN + ((i * 37) % 100) / 100 * (BIRDS.SIZE_MAX - BIRDS.SIZE_MIN);
      this.birds.push({
        x: Math.cos(a) * r, y: BIRDS.ALT_MIN, z: Math.sin(a) * r,
        heading: a + Math.PI / 2,
        speed: BIRDS.SPEED_MIN + ((i * 53) % 100) / 100 * (BIRDS.SPEED_MAX - BIRDS.SPEED_MIN),
        alt: BIRDS.ALT_MIN + ((i * 71) % 100) / 100 * (BIRDS.ALT_MAX - BIRDS.ALT_MIN),
        roll: 0, turn: 0, size,
        phase: (i * 2.399) % 6.283,
        // a small gull beats faster than a big one — the single cheapest "these are different birds" cue
        rate: 7.4 / (0.6 + size * 0.7),
        glide: 0, glideT: 1 + (i % 7) * 0.6,
        wander: 0,
      });
      this.flap[i * 4] = this.birds[i]!.phase;
      this.flap[i * 4 + 1] = this.birds[i]!.rate;
      this.flap[i * 4 + 3] = ((i * 29) % 100) / 100 * 0.5; // per-bird shade so the flock isn't one flat colour
    }
    parent.add(this.mesh);
  }

  /** Fade the flock (0 = gone: night, storm, Low preset). Below ~2% the whole system stops updating. */
  set opacity(v: number) { this._opacity = v; this.mat.uniforms.uOpacity!.value = v; this.mesh.visible = v > 0.02; }
  get opacity(): number { return this._opacity; }

  /**
   * Fly the flock for one frame. `camAz` is the direction the camera LOOKS (world radians) — most sub-flocks
   * are re-aimed inside that arc, so the birds stay part of the frame instead of wheeling behind your head
   * (they are atmosphere: a flock you never see is just CPU). `seaAt(x, z)` is the shared ocean height (so low
   * birds skim the REAL swell); `stormy` (0..1) makes them climb, tighten up and beat harder before they clear out.
   */
  /**
   * The islets the flock may adopt (world x/z + radius), nearest-first-ish. Render-only; the renderer
   * refreshes it on the same lazy clock it culls the world on. Gulls are LAND birds — a flock that only
   * ever wheels around the camera in open water is atmosphere, but a flock working an island is a landmark
   * you can navigate by, which is what the owner asked for.
   */
  setIslands(list: { x: number; z: number; r: number }[]): void { this.isles = list; }

  update(t: number, dt: number, camX: number, camZ: number, camAz: number, seaAt: (x: number, z: number) => number, stormy = 0, windDir = 0, windStrength = 0): void {
    if (!this.mesh.visible) return;
    const B = this.birds, n = B.length;
    // SUB-FLOCKS: one target for everybody made a single clump that was usually off-screen. Each group drifts
    // toward its own slowly wandering mark, retargeted on its own clock — so the sky always has birds in it.
    for (let g = 0; g < this.gTargetT.length; g++) {
      if (t <= (this.gTargetT[g] ?? 0)) continue;
      this.gTargetT[g] = t + 5 + Math.random() * 7;
      // AN ISLET IF THERE IS ONE. Gulls work the land; a group that finds an islet in reach adopts it and
      // rides a circuit around it (below) instead of wandering the open sea. Group 0 is left free so the
      // sky never empties when the nearest island is behind you.
      this.gIsle[g] = -1;
      if (g !== 0 && this.isles.length > 0 && Math.random() < 0.72 && stormy < 0.5) {
        let best = -1, bestD = BIRDS.ISLE_RANGE * BIRDS.ISLE_RANGE;
        for (let i = 0; i < this.isles.length; i++) {
          const isl = this.isles[i]!;
          const dx = isl.x - camX, dz = isl.z - camZ, d2 = dx * dx + dz * dz;
          if (d2 < bestD) { bestD = d2; best = i; }
        }
        if (best >= 0) {
          this.gIsle[g] = best;
          this.gIsleAng[g] = Math.random() * Math.PI * 2;
        }
      }
      if ((this.gIsle[g] ?? -1) >= 0) continue;   // the mark is derived per frame below
      // no islet: group 0 wanders anywhere, the rest are re-aimed into the camera's arc (±55°)
      const a = g === 0 ? Math.random() * Math.PI * 2 : camAz + (Math.random() - 0.5) * 1.9;
      const r = 22 + Math.random() * BIRDS.RANGE * 0.5;
      this.gTargetX[g] = camX + Math.cos(a) * r;
      this.gTargetZ[g] = camZ + Math.sin(a) * r;
    }
    // …and the adopted islets' marks WALK around them, every frame, so the group flies a real circuit.
    for (let g = 0; g < this.gIsle.length; g++) {
      const gi = this.gIsle[g] ?? -1;
      const isl = gi >= 0 ? this.isles[gi] : undefined;
      if (!isl) continue;
      this.gIsleAng[g] = (this.gIsleAng[g] ?? 0) + BIRDS.ISLE_ORBIT_RATE * dt * (g === 1 ? 1 : -1);
      const rr = isl.r * BIRDS.ISLE_ORBIT_MUL + BIRDS.ISLE_ORBIT_ADD;
      this.gTargetX[g] = isl.x + Math.cos(this.gIsleAng[g]!) * rr;
      this.gTargetZ[g] = isl.z + Math.sin(this.gIsleAng[g]!) * rr;
    }
    // one wind trim per frame is not enough (every bird has its own track), but the STRENGTH is shared
    const ws = windStrength * (1 + stormy * 0.6);
    // steering runs at THINK_HZ; the integration below runs every frame
    this.thinkAcc += dt;
    const think = this.thinkAcc >= 1 / BIRDS.THINK_HZ;
    const thinkDt = this.thinkAcc;
    if (think) this.thinkAcc = 0;
    for (let i = 0; i < n; i++) {
      const b = B[i]!;
      if (think) {
        // separation: push away from the nearest neighbour only (O(n) with a stride, not O(n²))
        let sx = 0, sz = 0;
        const o = B[(i + 1 + ((i * 7) % Math.max(1, n - 1))) % n]!;
        const dx = b.x - o.x, dz = b.z - o.z, d2 = dx * dx + dz * dz;
        if (d2 < BIRDS.SEP_DIST * BIRDS.SEP_DIST && d2 > 1e-4) { const d = Math.sqrt(d2); sx = dx / d; sz = dz / d; }
        b.wander += (Math.random() - 0.5) * 0.9;
        b.wander = Math.max(-1, Math.min(1, b.wander * 0.82));
        const g = i % this.gTargetX.length;
        steerBird(b, this.gTargetX[g] ?? camX, this.gTargetZ[g] ?? camZ, sx, sz, thinkDt);
        // beat/glide cycle: a gull works, then rides. Storm = work harder, glide less.
        b.glideT -= thinkDt;
        if (b.glideT <= 0) {
          const gliding = b.glide > 0.5;
          b.glide = gliding ? 0 : 1;
          b.glideT = gliding ? 0.8 + Math.random() * 1.8 : 1.4 + Math.random() * 3.4;
        }
      }
      // integrate flight (every frame, so motion is smooth between thinks)
      const gl = b.glide * (1 - stormy);
      // THE WIND. Ground speed answers to it and the airframe crabs + banks to hold the track (see windTrim).
      const trim = windTrim(b.heading, windDir, ws);
      const gs = b.speed * trim.drive;
      b.x += Math.cos(b.heading) * gs * dt;
      b.z += Math.sin(b.heading) * gs * dt;
      // altitude: ease toward the preferred band (higher in a storm), dip while banked, bob while beating
      const wantY = seaAt(b.x, b.z) + b.alt * (1 + stormy * 0.8) - Math.abs(b.roll) * 5;
      b.y += (wantY - b.y) * Math.min(1, dt * 0.9) + Math.sin(t * 1.3 + b.phase) * dt * 1.4;
      // recycle a bird that has strayed out of range: bring it back in upwind of the camera
      const ddx = b.x - camX, ddz = b.z - camZ;
      if (ddx * ddx + ddz * ddz > BIRDS.RANGE * BIRDS.RANGE) {
        const a = Math.atan2(ddz, ddx) + Math.PI + (Math.random() - 0.5) * 1.2;
        b.x = camX + Math.cos(a) * BIRDS.RANGE * 0.82;
        b.z = camZ + Math.sin(a) * BIRDS.RANGE * 0.82;
        b.heading = Math.atan2(camZ - b.z, camX - b.x);
      }
      const d = this.dummy;
      d.position.set(b.x, b.y, b.z);
      // yaw = the course PLUS the wind crab (the nose points upwind of the track — the cue), the X axis
      // carries the true WING BANK (the mesh's wings run along ±Z, so this is what drops one wingtip) and
      // the Z axis keeps the existing turn-lean. A gull holding station across a fresh breeze now flies
      // visibly sideways-on, tipped into the wind, and you can read the weather off it from the deck.
      d.rotation.set(trim.bank, -(b.heading + trim.crab), b.roll, 'YXZ');
      d.scale.setScalar(b.size);
      d.updateMatrix();
      this.mesh.setMatrixAt(i, d.matrix);
      this.flap[i * 4] = b.phase;
      this.flap[i * 4 + 1] = b.rate * (1 + stormy * 0.45);
      this.flap[i * 4 + 2] = gl;
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    (this.mesh.geometry.getAttribute('aFlap') as THREE.BufferAttribute).needsUpdate = true;
    this.mat.uniforms.uTime!.value = t;
  }

  /** Day/night + storm palette (called with the same values the sky uses). */
  setPalette(top: THREE.Color, under: THREE.Color): void {
    (this.mat.uniforms.uTop!.value as THREE.Color).copy(top);
    (this.mat.uniforms.uUnder!.value as THREE.Color).copy(under);
  }

  dispose(): void {
    this.mesh.parent?.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mat.dispose();
    this.mesh.dispose();
  }
}

/* ------------------------------------------------------------------------------------------------ *
 *  🐟 FLYING FISH — the bow wave's own startle response
 * ------------------------------------------------------------------------------------------------ */

/** Tuning for the flying-fish flush. Render-only; it never touches the sim, the wire or a gameplay read. */
export const FISH = {
  /** Concurrent fish. A flush is 3–6 of them, so this holds two or three overlapping bursts. */
  MAX: 18,
  /** Body length (m). Frankly exaggerated — a real flying fish is 25 cm, which against a 20 m brigantine
   *  is three pixels and reads as dirt on the lens. Sized to the same stylised scale the shoal fish and the
   *  gulls already use: big enough to recognise at 40 m, small enough that it is clearly not a dolphin. */
  LEN: 1.6,
  /** Seconds a glide lasts, and the gravity (m/s²) that shapes it. Deliberately WEAK: a real flying fish
   *  glides, it does not lob — a strong gravity gives you a leaping trout, which is a different animal. */
  LIFE: 1.7,
  G: 6.5,
  /** Hull speed (0..1 of top) below which nothing is startled, and the average seconds between flushes. */
  MIN_SPEED: 0.42,
  GAP: 7.5,
} as const;

/**
 * A shoal of flying fish bursting away from a hull's bow — ONE instanced draw call, the whole glide in the
 * vertex shader (ballistic arc + a tail beat + the roll of a banking fish), zero CPU per frame.
 *
 * WHY IT IS WORTH A DRAW CALL. It is the only thing in the game that tells you the sea is FULL rather than
 * merely large, and it costs nothing to be rare: fish are flushed by the BOW, so they only ever appear when
 * you are actually sailing, always ahead of you (i.e. in frame), and never twice in the same few seconds.
 * They are gone in a storm — a gale drives them down, and the frame is busy enough without them.
 *
 * The silhouette is four triangles: a slender body, and the two enormous pectoral fins that are the entire
 * reason a flying fish is recognisable at 30 m. Nose along +X, fins along ±Z, matching the gull rig.
 */
export class FlyingFish {
  readonly mesh: THREE.InstancedMesh;
  private mat: THREE.ShaderMaterial;
  private orig: Float32Array;
  private vel: Float32Array;
  private param: Float32Array;   // (birth, life, size, seed)
  private head = 0;
  private liveUntil = -1;
  private t = 0;
  private nextFlush = 4;
  private dirty = false;

  constructor(parent: THREE.Object3D) {
    const geo = buildFlyingFishGeometry(FISH.LEN);
    this.orig = new Float32Array(FISH.MAX * 3);
    this.vel = new Float32Array(FISH.MAX * 3);
    this.param = new Float32Array(FISH.MAX * 4);
    geo.setAttribute('aOrig', new THREE.InstancedBufferAttribute(this.orig, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aVel', new THREE.InstancedBufferAttribute(this.vel, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aFish', new THREE.InstancedBufferAttribute(this.param, 4).setUsage(THREE.DynamicDrawUsage));
    this.mat = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        { uTime: { value: 0 }, uBack: { value: new THREE.Color(0x3f6a86) }, uBelly: { value: new THREE.Color(0xf2f8fc) }, uOpacity: { value: 1 } },
      ]),
      vertexShader: /* glsl */ `
        #include <common>
        #include <fog_pars_vertex>
        attribute vec3 aOrig, aVel;
        attribute vec4 aFish;      // x birth, y life, z size, w seed
        attribute float aTail;     // 0 at the head … 1 at the tail tip (the only vertex that beats)
        attribute float aFin;      // 1 on the pectoral fins
        uniform float uTime;
        varying float vUp, vFin;
        void main(){
          float age = uTime - aFish.x;
          if (aFish.y <= 0.0 || age < 0.0 || age > aFish.y) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
          float k = age / aFish.y;
          // the glide: launched out of the water and settling back into it inside its own life
          vec3 c = aOrig + aVel * age - vec3(0.0, 0.5 * ${FISH.G.toFixed(2)} * age * age, 0.0);
          // orient along the CURRENT velocity, so the fish noses over as the arc comes down
          vec3 v = aVel - vec3(0.0, ${FISH.G.toFixed(2)} * age, 0.0);
          vec2 f2 = normalize(v.xz + vec2(1e-4, 0.0));
          vec3 fwd = vec3(f2.x, clamp(v.y / max(length(v), 1e-3), -0.9, 0.9), f2.y);
          fwd = normalize(fwd);
          vec3 rgt = normalize(cross(vec3(0.0, 1.0, 0.0), fwd));
          vec3 upv = cross(fwd, rgt);
          vec3 p = position * aFish.z;
          // the tail beats hard on the way up (it is still sculling the surface) and stills in the glide
          float beat = sin(uTime * 34.0 + aFish.w * 6.283) * (1.0 - smoothstep(0.05, 0.35, k)) * 0.55;
          p.z += beat * aTail * ${FISH.LEN.toFixed(2)} * aFish.z;
          // and it banks, so a burst is a fan of angles instead of a row of identical darts
          float roll = sin(aFish.w * 12.9) * 0.6;
          vec3 r2 = rgt * cos(roll) + upv * sin(roll);
          vec3 u2 = upv * cos(roll) - rgt * sin(roll);
          vec3 world = c + fwd * p.x + u2 * p.y + r2 * p.z;
          vUp = clamp(0.5 + p.y * 3.0, 0.0, 1.0);
          vFin = aFin;
          vec4 mvPosition = viewMatrix * vec4(world, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          #include <fog_vertex>
        }
      `,
      fragmentShader: /* glsl */ `
        precision mediump float;
        #include <common>
        #include <fog_pars_fragment>
        uniform vec3 uBack, uBelly; uniform float uOpacity;
        varying float vUp, vFin;
        void main(){
          if (uOpacity < 0.01) discard;
          // countershaded: dark back, silver belly — the marking that makes a 10 px shape read as a fish
          vec3 col = mix(uBelly, uBack, vUp);
          gl_FragColor = vec4(pow(col, vec3(2.2)), uOpacity * (vFin > 0.5 ? 0.72 : 1.0));
          #include <fog_fragment>
        }
      `,
      transparent: true, depthWrite: true, side: THREE.DoubleSide, fog: true,
    });
    this.mesh = new THREE.InstancedMesh(geo, this.mat, FISH.MAX);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    const idm = new THREE.Matrix4();
    for (let i = 0; i < FISH.MAX; i++) this.mesh.setMatrixAt(i, idm);
    this.mesh.instanceMatrix.needsUpdate = true;
    parent.add(this.mesh);
  }

  /** Fade with the rest of the fauna (0 = gone: storm, Low preset, night). */
  set opacity(v: number) { this.mat.uniforms.uOpacity!.value = v; }

  /**
   * One call per frame. `(bx, bz)` is the local hull's BOW, `heading` its course, `speed01` how hard she is
   * driving, `seaAt` the shared ocean height. Fires a flush on its own random clock and never while the
   * fauna is faded out — so nothing is ever spawned that cannot be seen.
   */
  update(t: number, bx: number, bz: number, heading: number, speed01: number, seaAt: (x: number, z: number) => number, enabled: boolean): void {
    this.t = t;
    this.mat.uniforms.uTime!.value = t;
    this.mesh.visible = t <= this.liveUntil;
    if (this.dirty) {
      const g = this.mesh.geometry;
      (g.getAttribute('aOrig') as THREE.BufferAttribute).needsUpdate = true;
      (g.getAttribute('aVel') as THREE.BufferAttribute).needsUpdate = true;
      (g.getAttribute('aFish') as THREE.BufferAttribute).needsUpdate = true;
      this.dirty = false;
    }
    if (!enabled || speed01 < FISH.MIN_SPEED) { this.nextFlush = Math.max(this.nextFlush, t + 2); return; }
    if (t < this.nextFlush) return;
    this.nextFlush = t + FISH.GAP * (0.55 + Math.random());
    // the shoal breaks AWAY from the bow, to one side, on a shallow diverging fan
    const side = Math.random() < 0.5 ? 1 : -1;
    const n = 3 + ((Math.random() * 4) | 0);
    const px = -Math.sin(heading) * side, pz = Math.cos(heading) * side;   // the beam, to that side
    const ox = bx + Math.cos(heading) * 5 + px * 2.5, oz = bz + Math.sin(heading) * 5 + pz * 2.5;
    for (let i = 0; i < n; i++) {
      const jx = ox + (Math.random() - 0.5) * 4, jz = oz + (Math.random() - 0.5) * 4;
      const away = heading + side * (0.55 + Math.random() * 0.55);
      const sp = 7.5 + Math.random() * 4.5;
      this.spawn(jx, seaAt(jx, jz) + 0.15, jz,
        Math.cos(away) * sp, 4.6 + Math.random() * 1.8, Math.sin(away) * sp,
        0.78 + Math.random() * 0.5);
    }
  }

  private spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number): void {
    const i = this.head;
    this.head = (this.head + 1) % FISH.MAX;
    const o = i * 3, q = i * 4;
    this.orig[o] = x; this.orig[o + 1] = y; this.orig[o + 2] = z;
    this.vel[o] = vx; this.vel[o + 1] = vy; this.vel[o + 2] = vz;
    this.param[q] = this.t; this.param[q + 1] = FISH.LIFE; this.param[q + 2] = size; this.param[q + 3] = Math.random();
    this.liveUntil = Math.max(this.liveUntil, this.t + FISH.LIFE);
    this.dirty = true;
  }

  /** Kill everything on the spot (stepping ashore / a scene teardown). */
  clear(): void {
    this.param.fill(0);
    this.liveUntil = -1;
    this.dirty = true;
    this.mesh.visible = false;
  }

  dispose(): void {
    this.mesh.parent?.remove(this.mesh);
    this.mesh.geometry.dispose(); this.mat.dispose(); this.mesh.dispose();
  }
}

/**
 * Flying-fish geometry: a slender body (nose +X, tail −X) and the two outsized pectoral fins that are the
 * entire silhouette. 4 triangles. `aTail` marks what the beat moves; `aFin` lets the fragment make the fins
 * translucent, which is what stops four flat triangles reading as a paper dart.
 */
export function buildFlyingFishGeometry(len: number = FISH.LEN): THREE.BufferGeometry {
  const L = len, pos: number[] = [], tail: number[] = [], fin: number[] = [];
  const push = (x: number, y: number, z: number, tl: number, fn: number): void => { pos.push(x, y, z); tail.push(tl); fin.push(fn); };
  // body: a flat lozenge, nose to tail, with a little depth so it catches the light from below
  push(L * 0.5, 0, 0, 0, 0); push(-L * 0.1, L * 0.09, 0, 0.2, 0); push(-L * 0.1, -L * 0.09, 0, 0.2, 0);
  // tail fork
  push(-L * 0.1, L * 0.06, 0, 0.2, 0); push(-L * 0.55, L * 0.16, 0, 1, 0); push(-L * 0.1, -L * 0.06, 0, 0.2, 0);
  // pectoral fins — the wings. Long, swept back, and they are most of what you see.
  for (const s of [1, -1]) {
    push(L * 0.24, 0, s * L * 0.03, 0, 1);
    push(-L * 0.30, 0.01, s * L * 0.62, 0.1, 1);
    push(-L * 0.06, 0, s * L * 0.10, 0, 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('aTail', new THREE.BufferAttribute(new Float32Array(tail), 1));
  g.setAttribute('aFin', new THREE.BufferAttribute(new Float32Array(fin), 1));
  return g;
}
